import { useCallback, useEffect, useRef, useState } from "react";
import {
    fetchAdminNotifications,
    markAllNotificationsRead,
    markNotificationRead,
} from "./notificationsApi";

/**
 * How often the unread badge is refreshed. The admin app has no socket
 * connection, so the centre polls; incidents are raised by a daily beat task,
 * which makes a one-minute cadence more than responsive enough.
 */
const POLL_INTERVAL_MS = 60000;

/**
 * Normalizes one notification row into safe values.
 * @param {Object} row - Raw notification from the API
 * @returns {Object}
 */
function normalizeRow(row) {
    return {
        id: row.id,
        type: row.type || "",
        subject: row.subject || "",
        read: Boolean(row.read),
        payload: row.payload && typeof row.payload === "object" ? row.payload : null,
        createdAt: row.created_at || null,
    };
}

/**
 * Normalizes the API response into a list plus counters.
 * @param {*} data - Decrypted API payload
 * @returns {Object}
 */
function normalizeList(data) {
    if (!data || typeof data !== "object") return { items: [], unread: 0, total: 0 };
    return {
        items: Array.isArray(data.items) ? data.items.map(normalizeRow) : [],
        unread: Number(data.unread) || 0,
        total: Number(data.total) || 0,
    };
}

/**
 * Loads the notification list into state, ignoring aborted requests.
 * @param {Object} args - Loader arguments
 */
async function loadNotifications({ signal, setState, setError, setLoading }) {
    setLoading(true);
    setError(null);
    try {
        const data = await fetchAdminNotifications({ pageSize: 50 });
        if (!signal.aborted) setState(normalizeList(data));
    } catch (err) {
        if (!signal.aborted) setError(err?.response?.data || "Impossible de charger les notifications");
    } finally {
        if (!signal.aborted) setLoading(false);
    }
}

/**
 * Custom hook driving the admin notification centre: list, unread badge,
 * polling and read acknowledgements.
 * @returns {Object} state and actions for the notification centre
 */
function useAdminNotifications() {
    const [state, setState] = useState({ items: [], unread: 0, total: 0 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reloadToken, setReloadToken] = useState(0);
    const abortRef = useRef(null);

    const refresh = useCallback(() => setReloadToken((token) => token + 1), []);

    useEffect(() => {
        const controller = new AbortController();
        abortRef.current = controller;
        loadNotifications({ signal: controller.signal, setState, setError, setLoading });
        return () => controller.abort();
    }, [reloadToken]);

    useEffect(() => {
        const timer = setInterval(refresh, POLL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [refresh]);

    const markRead = useCallback(async (notificationId) => {
        await markNotificationRead(notificationId);
        setState((prev) => ({
            ...prev,
            items: prev.items.map((row) => (row.id === notificationId ? { ...row, read: true } : row)),
            unread: Math.max(0, prev.unread - 1),
        }));
    }, []);

    const markAllRead = useCallback(async () => {
        await markAllNotificationsRead();
        setState((prev) => ({
            ...prev,
            items: prev.items.map((row) => ({ ...row, read: true })),
            unread: 0,
        }));
    }, []);

    return { ...state, loading, error, refresh, markRead, markAllRead };
}

export default useAdminNotifications;
